import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Stepper, Typography } from '@mui/material';
import { UsageHistory } from 'common/modules/usageHistory/types';
import { OrderInfo, LoadingData } from 'common/types';
import SkeletonList from 'components/SkeletonList';
import UsageHistoryStep from './UsageHistoryStep';
import ErrorState from 'common/components/states/ErrorState';
import { useTranslation } from 'services/localization';
import { InventoryItem } from 'common/modules/inventory';
import LoadMoreStep from './LoadMoreStep';
import errorHandler from 'common/services/errorHandler';
import { useSelector } from 'react-redux';
import { buildRentalsOfTypeData, RentalsType } from 'selectors/RentalsSelectors';
import { notUndefined } from 'common/utils/common';
import { getUsageHistory } from './utils';

interface Props {
	item: InventoryItem;
	shopId: string;
}

const activeOrdersSelector = buildRentalsOfTypeData(RentalsType.ACTIVE);

const ITEMS_TO_SHOW_INCREMENT = 5;

const getActiveOrders = (allActiveOrders: OrderInfo[], orderIds: string[]) => {
	return orderIds.map((id) => allActiveOrders.find((o) => o.id === id)).filter(notUndefined);
};

const UsageHistoryList = (props: Props) => {
	const { item, shopId } = props;
	const { id: itemId } = item;
	const { t } = useTranslation();
	const allActiveOrders = useSelector(activeOrdersSelector);
	const allActiveItemOrders = getActiveOrders(allActiveOrders, item.activeOrderIds ?? []);
	const [visibleActiveOrdersCount, setVisibleActiveOrdersCount] = useState<number>(
		Math.min(ITEMS_TO_SHOW_INCREMENT, allActiveItemOrders.length),
	);
	const visibleActiveOrders = allActiveItemOrders.slice(0, visibleActiveOrdersCount);
	const [usageHistory, setUsageHistory] = useState<LoadingData<UsageHistory[] | null>>({
		loading: false,
		data: null,
		error: null,
	});
	const [nextQueryStartAfter, setNextQueryStartAfter] = useState<LoadingData<string | null>>({
		loading: false,
		data: null,
		error: null,
	});
	const initialActiveOrdersLength = useRef(allActiveItemOrders.length);
	const showMoreButtonVisible =
		!!nextQueryStartAfter.data ||
		allActiveItemOrders.length > visibleActiveOrdersCount ||
		usageHistory.data === null;

	const emptyActivity = !visibleActiveOrders.length && !usageHistory.data?.length;

	const getNextItemsToLoad = useCallback(
		(itemsToLoad: number) => {
			const hiddenActiveOrdersCount = Math.max(
				0,
				allActiveItemOrders.length - visibleActiveOrdersCount,
			);
			const activeOrdersToLoad = Math.min(itemsToLoad, hiddenActiveOrdersCount);
			const historyItemsToLoad = itemsToLoad - activeOrdersToLoad;
			return {
				active: activeOrdersToLoad,
				history: historyItemsToLoad,
			};
		},
		[allActiveItemOrders.length, visibleActiveOrdersCount],
	);

	const fetchUsageHistory = useCallback(
		async (itemId: string, itemsToLoad: number) => {
			setUsageHistory({ loading: true, data: null, error: null });
			try {
				const newUsageHistory = await getUsageHistory({
					shopId,
					itemId,
					startAfter: undefined,
					limit: itemsToLoad,
				});
				setUsageHistory({ loading: false, data: newUsageHistory, error: null });
				const queryLimitNotReached = newUsageHistory.length < itemsToLoad;
				const _nextQueryStartAfter = queryLimitNotReached
					? null
					: newUsageHistory[newUsageHistory.length - 1].endDate;
				setNextQueryStartAfter({ loading: false, data: _nextQueryStartAfter, error: null });
			} catch (e) {
				errorHandler.report(e);
				setUsageHistory({ loading: false, data: null, error: 'Error' });
			}
		},
		[shopId],
	);

	useEffect(() => {
		const historyItemsToLoad = Math.max(
			0,
			ITEMS_TO_SHOW_INCREMENT - initialActiveOrdersLength.current,
		);
		if (!!historyItemsToLoad) {
			fetchUsageHistory(itemId, historyItemsToLoad);
		}
	}, [fetchUsageHistory, itemId]);

	const loadMoreUsage = async (itemsToLoad: number) => {
		const nextItemsToLoad = getNextItemsToLoad(itemsToLoad);
		if (nextItemsToLoad.active > 0) {
			setVisibleActiveOrdersCount((prev) => prev + nextItemsToLoad.active);
		}
		if (nextItemsToLoad.history <= 0) return;
		try {
			setNextQueryStartAfter((prev) => ({ loading: true, data: prev.data, error: null }));
			const newUsageHistory = await getUsageHistory({
				shopId,
				itemId,
				startAfter: nextQueryStartAfter.data ?? undefined,
				limit: nextItemsToLoad.history,
			});
			setUsageHistory({
				loading: false,
				data: [...(usageHistory.data ?? []), ...newUsageHistory],
				error: null,
			});
			const queryLimitNotReached = newUsageHistory.length < nextItemsToLoad.history;
			const _nextQueryStartAfter = queryLimitNotReached
				? null
				: newUsageHistory[newUsageHistory.length - 1].endDate;
			setNextQueryStartAfter({ loading: false, data: _nextQueryStartAfter, error: null });
		} catch (e) {
			errorHandler.report(e);
			setUsageHistory({
				loading: false,
				data: null,
				error: t('common:errors.error'),
			});
			setNextQueryStartAfter({ loading: false, data: null, error: t('common:errors.error') });
		}
	};

	const getCustomerName = (customerName: { firstName: string; lastName: string }) => {
		return `${customerName.firstName} ${customerName.lastName}`;
	};

	if (!!usageHistory.loading) {
		return <SkeletonList rows={3} rowHeight={40} />;
	}

	if (!!usageHistory.error) {
		return (
			<ErrorState
				t={t}
				ctaButton={{
					onClick: () => fetchUsageHistory(itemId, ITEMS_TO_SHOW_INCREMENT),
					variant: 'text',
				}}
			/>
		);
	}

	if (emptyActivity) {
		return (
			<Typography variant="body2" color="secondary">
				{t('inventory.article.noActivity', 'This article has not been used yet')}
			</Typography>
		);
	}

	return (
		<Stepper nonLinear orientation="vertical" connector={null}>
			{visibleActiveOrders.map((order, orderIndex) => (
				<UsageHistoryStep
					key={order.id}
					orderId={order.id}
					customerName={order.shopperNames[0]}
					endDate={null}
					orderNumber={order.orderNumber}
					lastStep={
						!showMoreButtonVisible &&
						!usageHistory.data?.length &&
						orderIndex === visibleActiveOrders.length - 1
					}
					active={true}
				/>
			))}
			{usageHistory.data?.map((usage, usageIndex) => (
				<UsageHistoryStep
					key={usage.id}
					orderId={usage.orderId}
					customerName={
						usage.responsiblePerson ? getCustomerName(usage.responsiblePerson) : undefined
					}
					endDate={usage.endDate}
					orderNumber={usage.orderNumber ?? undefined}
					lastStep={!showMoreButtonVisible && usageIndex === usageHistory.data!.length - 1}
				/>
			))}
			{showMoreButtonVisible && (
				<LoadMoreStep
					count={ITEMS_TO_SHOW_INCREMENT}
					onClick={() => loadMoreUsage(ITEMS_TO_SHOW_INCREMENT)}
					loading={nextQueryStartAfter.loading}
				/>
			)}
		</Stepper>
	);
};

export default UsageHistoryList;
